import classNames from "classnames";
import * as React from "react";

import styles from "./ResourceFormActions.module.css";

/**
 * How a notice reads: a failure, something the person has to check, or a plain
 * report that needs neither.
 */
export type ResourceFormNoticeTone = "error" | "warning" | "neutral";

export interface ResourceFormNoticeProps extends Omit<
  React.ComponentPropsWithoutRef<"div">,
  "title"
> {
  /** @default "error" */
  tone?: ResourceFormNoticeTone;
  /** A first line in bold, with the children under it. */
  title?: string;
}

/**
 * The box a form shows directly above its actions row: why the last
 * submission failed, or what it left behind. It carries no role of its own;
 * pass `role="alert"` for a failure that must interrupt, `role="status"` for a
 * report.
 */
export function ResourceFormNotice({
  tone = "error",
  title,
  className,
  children,
  ...props
}: ResourceFormNoticeProps) {
  return (
    <div
      {...props}
      className={classNames(styles.notice, className)}
      data-tone={tone}
    >
      {title !== undefined && <p className={styles.noticeTitle}>{title}</p>}
      {children}
    </div>
  );
}

/**
 * The line on the left of the row, saying what is left to do or what is
 * happening. A `status` region, so a change to it is announced.
 */
export function ResourceFormStatus(
  props: Omit<React.ComponentPropsWithoutRef<"p">, "className" | "role">,
) {
  return <p {...props} className={styles.status} role="status" />;
}

export interface ResourceFormActionsRowProps extends Omit<
  React.ComponentPropsWithoutRef<"div">,
  "children"
> {
  /** Above everything else: a {@link ResourceFormNotice}, or nothing. */
  notice?: React.ReactNode;
  /** Directly above the row, such as a progress bar. */
  progress?: React.ReactNode;
  /** The left of the row, usually a {@link ResourceFormStatus}. */
  status: React.ReactNode;
  /** The right of the row: the way out, then the submit. */
  buttons: React.ReactNode;
}

/**
 * The layout a form's main column ends on, with nothing of any form in it: a
 * notice, then a status on the left and buttons on the right.
 * {@link ResourceFormActions} fills it from a react-hook-form form; a form
 * that is not one fills it itself, so every form page ends on the same row.
 */
export function ResourceFormActionsRow({
  notice,
  progress,
  status,
  buttons,
  className,
  ...props
}: ResourceFormActionsRowProps) {
  return (
    <div {...props} className={classNames(styles.actions, className)}>
      {notice}
      {progress}
      <div className={styles.row}>
        {status}
        <div className={styles.buttons}>{buttons}</div>
      </div>
    </div>
  );
}
